/**
 * Where the Marketing Suite lives. It is its own product with its own login,
 * so a coach who holds it opens it here, in a new tab; everyone else gets the
 * in-app sales page.
 *
 * Aliases: marketing suite url, gohighlevel app, ghl login, suite sales page
 */

export const MARKETING_SUITE_URL = "https://app.dreamcoachmatch.com";

export const MARKETING_SUITE_PAGE_PATH =
  "/my-coaching/business/marketing-suite";

export const MARKETING_SUITE_INTRO =
  "Your CRM, inbox, bookings, funnels, courses, email and automations, connected and set up for coaches. Get it on its own, or bundled with NOVA as NOVA Pro.";

/**
 * The tools the Suite replaces, one job per row, in the order of the cost
 * table. The monthly figures are the publicly listed prices of the tools,
 * typed as the design states them; they and the total are not worked out.
 */
export const SUITE_REPLACES = [
  {
    job: "Courses + memberships",
    tools: ["Kajabi", "Teachable", "Thinkific", "Patreon"],
    monthlyCost: 179,
  },
  {
    job: "Community",
    tools: ["Skool", "Podia", "Circle", "Mighty Networks"],
    monthlyCost: 99,
  },
  {
    job: "Funnels",
    tools: ["ClickFunnels", "Leadpages"],
    monthlyCost: 97,
  },
  {
    job: "Website builder",
    tools: ["WordPress", "Wix", "Squarespace"],
    monthlyCost: 39,
  },
  {
    job: "Scheduling",
    tools: ["Calendly", "Acuity", "Cal.com"],
    monthlyCost: 12,
  },
  {
    job: "Email marketing",
    tools: ["Mailchimp", "ActiveCampaign", "Kit (ConvertKit)"],
    monthlyCost: 60,
  },
  { job: "SMS", tools: ["SimpleTexting"], monthlyCost: 39 },
  {
    job: "CRM",
    tools: ["HubSpot", "Keap", "Kartra"],
    monthlyCost: 299,
  },
  {
    job: "Client admin + contracts",
    tools: ["Dubsado", "HoneyBook", "DocuSign"],
    monthlyCost: 59,
  },
  { job: "Automations", tools: ["Zapier"], monthlyCost: 30 },
  {
    job: "Intake forms",
    tools: ["Typeform", "Jotform"],
    monthlyCost: 39,
  },
  { job: "Link-in-bio", tools: ["Linktree"], monthlyCost: 15 },
  {
    job: "Social scheduling",
    tools: ["Later", "Metricool"],
    monthlyCost: 25,
  },
  {
    job: "Ad management",
    tools: ["AdEspresso", "Madgicx"],
    monthlyCost: 49,
  },
] as const;

/** The typical cost of the whole stack, as the design states it. */
export const SUITE_REPLACES_TOTAL = "$1,041/mo";

export const SUITE_CLOSING_LINES = [
  "No more broken connections.",
  "No more copy-pasting.",
  "No more managing 10 different logins.",
] as const;

export const SUITE_CLOSING_HIGHLIGHT = "Everything in one place.";

export const SUITE_COST_SMALL_PRINT =
  "Pricing based on publicly listed monthly-billing plans as of October 2026. Savings vary by usage. All trademarks belong to their respective owners.";

export const SUITE_FEATURES = [
  {
    name: "CRM and pipeline",
    body: "Every lead and client in one place, at the stage they are really at — invited, booked, offer made, won.",
  },
  {
    name: "One inbox",
    body: "Email, text, Instagram and Facebook messages in a single thread per person, so nothing gets lost between apps.",
  },
  {
    name: "Bookings and calendar",
    body: "Your availability, booking links and reminders, with payment taken at booking if you want it.",
  },
  {
    name: "Funnels, website and forms",
    body: "Your offer page, signup pages, intake forms and link-in-bio, all on one domain.",
  },
  {
    name: "Courses, memberships and community",
    body: "Host your program, sell memberships and run your client community without a separate platform.",
  },
  {
    name: "Email and SMS campaigns",
    body: "Newsletters and sequences to your own list, sent from your own address and number.",
  },
  {
    name: "Automations",
    body: "Follow-ups, no-show nudges and second asks run on their own, with no Zaps to break.",
  },
  {
    name: "Social planner",
    body: "Schedule posts to your accounts from the same place your leads land.",
  },
  {
    name: "Ad manager",
    body: "Launch Meta and Google ads, with leads going straight into your pipeline.",
  },
] as const;
